import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import {
    Button,
    Divider,
    Grid,
    IconButton,
    ListItemIcon,
    MenuItem,
    MenuList,
    Stack,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import { useModal } from 'mui-modal-provider';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    useDeleteSavedFilter,
    useUpdateSavedFilter,
} from '../../../sic-lib/api/hooks/useSavedFilters';
import {
    CHANNEL_TYPE_LABELS,
    INBOX_DATE_RANGE_LABELS,
    INBOX_DEFAULT_SORT,
    OWNERSHIP_LABELS,
} from '../../../sic-lib/defaults';
import { InboxFilter } from '../../../sic-lib/types/InboxTypes';
import { useAppDispatch } from '../../../store/hooks';
import { clearFilters, setFilters } from '../../../store/inbox';
import { updateUserInboxFilter } from '../../../store/users';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import DialogConfirmAction from '../../Shared/DialogConfirmAction/DialogConfirmAction';
import { DrawerCommon } from '../../Shared/Drawer/DrawerCommon';
import IconButtonLoading from '../../Shared/IconButtonLoading/IconButtonLoading';
import ToggleComponents from '../../Shared/ToggleComponents/ToggleComponents';

interface Props {
    isOpen: boolean;
    currentInboxFilter: InboxFilter;
    savedFilters: InboxFilter[] | undefined;
    setShowFilterDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    setShowSavedFiltersDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NewRecentSavedFiltersDrawer = ({
    currentInboxFilter,
    savedFilters,
    isOpen,
    setShowFilterDrawer,
    setShowSavedFiltersDrawer,
}: Props) => {
    const { showModal } = useModal();
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const {
        mutateAsync: deleteFilter,
        isSuccess: isDeleteSuccess,
        error: deleteError,
    } = useDeleteSavedFilter();
    const {
        mutateAsync: updateFilter,
        isSuccess: isUpdateSuccess,
        error: updateError,
    } = useUpdateSavedFilter();

    const handleClickApply = (filter: InboxFilter) => {
        setShowFilterDrawer(false);
        setShowSavedFiltersDrawer(false);
        dispatch(setFilters({ filter }));
    };

    const handleClickEdit = (filter: InboxFilter) => {
        setShowFilterDrawer(true);
        setShowSavedFiltersDrawer(false);
        dispatch(setFilters({ filter }));
    };

    const handleDelete = async (filter: InboxFilter) => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Delete Filter',
            description: 'This filter will be permanently deleted',
            onConfirm: async () => {
                try {
                    await deleteFilter(filter.id!);

                    if (filter.isDefault) {
                        dispatch(updateUserInboxFilter());
                    }
                    if (filter.id === currentInboxFilter.id) {
                        dispatch(clearFilters());
                    }
                } catch (error: any) {
                    console.log(error);
                }
            },
            onCancel: () => {
                modal.hide();
            },
        });
    };
    const handleDefaultChange = async (filter: InboxFilter) => {
        try {
            await updateFilter({ ...filter, isDefault: !filter.isDefault });

            dispatch(updateUserInboxFilter());
        } catch (error: any) {
            if (error.isFormError) {
                console.log(error);
                //replace with toast
            }
        }
    };

    const handleNameChange = async (
        filter: InboxFilter,
        event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    ) => {
        try {
            updateFilter({
                ...filter,
                name: event?.currentTarget.value || filter.name,
            });

            if (isUpdateSuccess) {
                if (filter.isDefault) {
                    dispatch(updateUserInboxFilter());
                }
            }
        } catch (error: any) {
            if (error.isFormError) {
                console.log(error);
                //replace with toast
            }
        }
    };

    return (
        <DrawerCommon
            open={isOpen}
            handleOnClose={() => setShowSavedFiltersDrawer(false)}
            title="New/Recent: Saved Filters"
            width="700px"
            anchor="left"
            footer={
                <Stack
                    direction="row"
                    p={2}
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    <Button variant="outlined" onClick={() => setShowSavedFiltersDrawer(false)}>
                        Close
                    </Button>
                </Stack>
            }
        >
            <Stack spacing={2}>
                {!savedFilters?.length && (
                    <Typography variant="body1" align="center">
                        No Saved Filters
                    </Typography>
                )}
                <AxiosErrorAlert error={deleteError || updateError} />
                <MenuList disablePadding>
                    {savedFilters?.map(aFilter => (
                        <React.Fragment key={aFilter.id}>
                            <MenuItem
                                onClick={() => handleClickApply(aFilter)}
                                style={{
                                    overflow: 'hidden',
                                    whiteSpace: 'pre-wrap',
                                }}
                                selected={aFilter.id === currentInboxFilter.id}
                            >
                                <Grid key={aFilter.id} container item direction="column">
                                    <Grid
                                        container
                                        item
                                        direction="row"
                                        wrap="nowrap"
                                        justifyContent="space-between"
                                    >
                                        {/* STAR & NAME */}
                                        <Grid
                                            container
                                            item
                                            flex={8}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            wrap="nowrap"
                                        >
                                            <Grid
                                                item
                                                flex={8}
                                                container
                                                direction="row"
                                                alignItems="center"
                                                className={classes.gridHeader}
                                            >
                                                {/* STAR */}
                                                <Grid item>
                                                    <IconButton
                                                        onClick={(
                                                            e: React.MouseEvent<HTMLButtonElement>,
                                                        ) => {
                                                            e.stopPropagation();
                                                            handleDefaultChange(aFilter);
                                                        }}
                                                    >
                                                        <Tooltip
                                                            title={
                                                                aFilter.isDefault
                                                                    ? 'Remove Default Filter'
                                                                    : 'Set Filter as Default'
                                                            }
                                                        >
                                                            <ListItemIcon
                                                                className={classes.starIcon}
                                                            >
                                                                {aFilter.isDefault ? (
                                                                    <StarIcon />
                                                                ) : (
                                                                    <StarOutlineIcon />
                                                                )}
                                                            </ListItemIcon>
                                                        </Tooltip>
                                                    </IconButton>
                                                </Grid>
                                                {/* FILTER NAME */}
                                                <ToggleComponents>
                                                    {(toggle, toggleComponent) =>
                                                        !toggle ? (
                                                            <Grid item>
                                                                <Button
                                                                    onClick={(
                                                                        e: React.MouseEvent<HTMLButtonElement>,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        toggleComponent();
                                                                    }}
                                                                    sx={{
                                                                        color: 'rgba(0, 0, 0, 0.87)',
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                'transparent',
                                                                        },
                                                                    }}
                                                                >
                                                                    <Tooltip title="Edit Filter Name">
                                                                        <Grid item>
                                                                            <Typography
                                                                                variant="h6"
                                                                                className={
                                                                                    classes.filterName
                                                                                }
                                                                            >
                                                                                {aFilter.name}
                                                                            </Typography>
                                                                        </Grid>
                                                                    </Tooltip>
                                                                </Button>
                                                            </Grid>
                                                        ) : (
                                                            <Grid item>
                                                                <TextField
                                                                    onKeyUp={event => {
                                                                        if (event.key === 'Enter')
                                                                            toggleComponent();
                                                                    }}
                                                                    onBlur={(
                                                                        e: React.FocusEvent<HTMLInputElement>,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        toggleComponent();
                                                                    }}
                                                                    defaultValue={aFilter.name}
                                                                    autoFocus
                                                                    onClick={(
                                                                        e: React.MouseEvent,
                                                                    ) => e.stopPropagation()}
                                                                    onChange={(
                                                                        e: React.ChangeEvent<HTMLTextAreaElement>,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        handleNameChange(
                                                                            aFilter,
                                                                            e,
                                                                        );
                                                                    }}
                                                                />
                                                            </Grid>
                                                        )
                                                    }
                                                </ToggleComponents>
                                            </Grid>
                                        </Grid>
                                        {/* BUTTONS */}
                                        <Grid container item flex={4} direction="row-reverse">
                                            {/* DELETE */}
                                            <Grid item>
                                                <IconButtonLoading
                                                    tooltip="Delete"
                                                    showLoading={false}
                                                    onClick={(
                                                        e: React.MouseEvent<HTMLButtonElement>,
                                                    ) => {
                                                        e.stopPropagation();
                                                        handleDelete(aFilter);
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButtonLoading>
                                            </Grid>
                                            {/* EDIT */}
                                            <Grid item>
                                                <IconButtonLoading
                                                    tooltip="Edit filter"
                                                    showLoading={false}
                                                    onClick={(
                                                        e: React.MouseEvent<HTMLButtonElement>,
                                                    ) => {
                                                        e.stopPropagation();
                                                        handleClickEdit(aFilter);
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButtonLoading>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Tooltip title={`Apply "${aFilter.name}" Filter`}>
                                        <Grid container item direction="column" spacing={1}>
                                            <Grid container item direction="row">
                                                <Grid item xs={12} sm={3}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '.9rem',
                                                            marginRight: '.5rem',
                                                        }}
                                                    >
                                                        Date
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={9}>
                                                    <Typography
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontSize: '.8rem',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >{`Activity Date: ${
                                                        INBOX_DATE_RANGE_LABELS[
                                                            aFilter.filter.activityRange
                                                        ]
                                                    }, Registration Date: ${
                                                        INBOX_DATE_RANGE_LABELS[
                                                            aFilter.filter.registrationRange
                                                        ]
                                                    }, Channel Date: ${
                                                        INBOX_DATE_RANGE_LABELS[
                                                            aFilter.filter.channelsRange
                                                        ]
                                                    }`}</Typography>
                                                </Grid>
                                            </Grid>

                                            <Grid container item direction="row">
                                                <Grid item xs={12} sm={3}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '.9rem',
                                                            marginRight: '.5rem',
                                                        }}
                                                    >
                                                        Channels
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={9}>
                                                    <Typography
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontSize: '.8rem',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {aFilter.filter.channels
                                                            .map(itm => CHANNEL_TYPE_LABELS[itm])
                                                            .join(', ')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <Grid container item direction="row">
                                                <Grid item xs={12} sm={3}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '.9rem',
                                                            marginRight: '.5rem',
                                                        }}
                                                    >
                                                        Sort Order
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={9}>
                                                    <Typography
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontSize: '.8rem',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {
                                                            INBOX_DEFAULT_SORT.find(
                                                                itm =>
                                                                    itm.sortItem.field ===
                                                                        aFilter.filter.sort.field &&
                                                                    itm.sortItem.direction ===
                                                                        aFilter.filter.sort
                                                                            .direction,
                                                            )?.label
                                                        }
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <Grid container item direction="row">
                                                <Grid item xs={12} sm={3}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '.9rem',
                                                            marginRight: '.5rem',
                                                        }}
                                                    >
                                                        Ownership Scope
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={9}>
                                                    <Typography
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontSize: '.8rem',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {
                                                            OWNERSHIP_LABELS[
                                                                aFilter.filter.ownershipScope
                                                            ]
                                                        }
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Tooltip>
                                </Grid>
                            </MenuItem>
                            <Divider />
                        </React.Fragment>
                    ))}
                </MenuList>
            </Stack>
        </DrawerCommon>
    );
};

const useStyles = makeStyles()((theme: Theme) => ({
    gridHeader: {
        paddingLeft: theme.spacing(1),
    },
    filterName: {
        maxWidth: '12rem',
        wordWrap: 'break-word',
    },
    totalContacts: {
        marginRight: theme.spacing(6),
    },
    starIcon: {
        minWidth: 0,
        color: 'gold',
    },
}));
